import React, { useContext, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import ReusableSidebar from '../../components/sidebar/reusable-sidebar';
import { MainContent } from '../../components/sidebar/sidebar.styles';
import {
  useGetProjectById,
  useDeleteProject,
  useToggleProjectPublish,
  useSetProjectCover,
  useUploadProjectScreenshot,
  useDeleteProjectScreenshot,
} from '../../services/api/projects-api';
import Loading from '../../components/loading';
import { Button } from 'primereact/button';
import { Chip } from 'primereact/chip';
import { Tag } from 'primereact/tag';
import { Dialog } from 'primereact/dialog';
import { ToastContext } from '../../services/contexts/toast-context';
import ProjectFormDialog from './components/project-form-dialog';
import ScreenshotUploader from './components/screenshot-uploader';
import { EditOverviewDialog, AddSkillDialog, AddLinkDialog, RemoveSkillDialog, RemoveLinkDialog } from './components/quick-edit-dialogs';
import {
  AddChipButton,
  BackRow,
  ChipsWrap,
  ContentStack,
  DescriptionText,
  EmptyMessage,
  HeroCard,
  HeroContent,
  HeroCover,
  HeroTitleBlock,
  HeroToolbar,
  LinkRow,
  LinksList,
  NotFoundBox,
  NotFoundCard,
  PageWrapper,
  Panel,
  PanelHeader,
  PanelTitle,
  ScreenshotCard,
  ScreenshotDeleteButton,
  ScreenshotImage,
  ScreenshotLink,
  ScreenshotsGrid,
} from './project-detail.styles';

function parseLinks(linksValue) {
  if (!linksValue) return [];
  if (Array.isArray(linksValue)) {
    return linksValue.filter(link => link?.url);
  }

  if (typeof linksValue === 'string') {
    try {
      const parsed = JSON.parse(linksValue);
      return Array.isArray(parsed) ? parsed.filter(link => link?.url) : [];
    } catch {
      return [];
    }
  }

  return [];
}

// Maps each portfolio template id to the CSS variables this page reads,
// so the public project page echoes the owner's chosen template palette.
const TEMPLATE_THEMES = {
  1: { // Sabri — dark navy with blue accent
    '--bg-main': '#0a0a0a',
    '--bg-card': '#141414',
    '--text-main': '#f5f5f5',
    '--text-secondary': '#94a3b8',
    '--border-color': 'rgba(255, 255, 255, 0.14)',
    '--primary-color': '#60a5fa',
  },
  2: { // Classic — warm editorial paper tones
    '--bg-main': '#f5f4ef',
    '--bg-card': '#fffdf9',
    '--text-main': '#20201d',
    '--text-secondary': '#6f7069',
    '--border-color': '#deded5',
    '--primary-color': '#b65d3b',
  },
  3: { // Bandara — deep indigo with sky-blue accent
    '--bg-main': '#1f1f38',
    '--bg-card': '#2c2c6c',
    '--text-main': '#f0f0f0',
    '--text-secondary': '#c6c6e0',
    '--border-color': 'rgba(77, 181, 255, 0.4)',
    '--primary-color': '#4db5ff',
  },
  4: { // Opus — charcoal with gold accent
    '--bg-main': '#0b0d11',
    '--bg-card': 'rgba(255, 255, 255, 0.04)',
    '--text-main': '#f7f3eb',
    '--text-secondary': 'rgba(247, 243, 235, 0.68)',
    '--border-color': 'rgba(247, 243, 235, 0.12)',
    '--primary-color': '#d0a76f',
  },
};

export default function ProjectDetail() {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const { data: project, isLoading } = useGetProjectById(id);
  const toastRef = useContext(ToastContext);

  const [isSidebarOpen, setIsSidebarOpen] = useState(() =>
    typeof window === 'undefined' || !window.matchMedia('(max-width: 768px)').matches
  );
  const [showFormDialog, setShowFormDialog] = useState(false);
  const [showOverviewDialog, setShowOverviewDialog] = useState(false);
  const [showAddSkillDialog, setShowAddSkillDialog] = useState(false);
  const [showAddLinkDialog, setShowAddLinkDialog] = useState(false);
  const [screenshotModalVisible, setScreenshotModalVisible] = useState(false);
  const [deleteConfirmVisible, setDeleteConfirmVisible] = useState(false);
  const [skillToRemove, setSkillToRemove] = useState(null);
  const [linkToRemove, setLinkToRemove] = useState(null);

  const { mutate: deleteProject, isPending: isDeleting } = useDeleteProject();
  const { mutate: togglePublish, isPending: isToggling } = useToggleProjectPublish();
  const { mutate: setProjectCover, isPending: isSettingCover } = useSetProjectCover();
  const { mutateAsync: deleteScreenshot, isPending: isDeletingScreenshot } = useDeleteProjectScreenshot();

  const isPublicProjectRoute = location.pathname.startsWith('/projects/');

  if (isLoading) return <Loading />;
  if (!project) {
    return (
      <>
        {!isPublicProjectRoute && (
          <ReusableSidebar
            isSidebarOpen={isSidebarOpen}
            handleToggleSidebar={() => setIsSidebarOpen(v => !v)}
            selectedPage="projects"
          />
        )}
        <MainContent isSidebarOpen={!isPublicProjectRoute && isSidebarOpen}>
          <PageWrapper>
            <NotFoundBox>
              <NotFoundCard>
                <h3>Project not found</h3>
                <p>The project may have been removed or is no longer available.</p>
                <Button
                  label="Back to Projects"
                  icon="pi pi-arrow-left"
                  onClick={() => navigate(-1)}
                />
              </NotFoundCard>
            </NotFoundBox>
          </PageWrapper>
        </MainContent>
      </>
    );
  }

  // Determine cover image: use explicit cover, or first screenshot, or placeholder
  const coverImage = project.cover_image_url ||
    (project.screenshots_urls && project.screenshots_urls.length > 0
      ? project.screenshots_urls[0] 
      : null);
  const links = parseLinks(project.links);
  const screenshots = Array.isArray(project.screenshots_urls) ? project.screenshots_urls : [];
  const hasDescription = project.description && project.description.trim().length > 0;
  const isOwner = !!project.is_owner;
  const themeVars = isPublicProjectRoute
    ? { ...TEMPLATE_THEMES[project.selected_template], marginLeft: 0 }
    : null;

  const handleTogglePublish = () => {
    togglePublish(
      { projectId: project.id, isPublished: !project.is_published },
      {
        onSuccess: () => {
          toastRef.current.show({
            severity: 'success',
            summary: 'Success',
            detail: !project.is_published ? 'Project published' : 'Project unpublished',
            life: 3000,
          });
        },
        onError: (error) => {
          toastRef.current.show({
            severity: 'error',
            summary: 'Error',
            detail: error.message || 'Failed to update project',
            life: 3000,
          });
        },
      }
    );
  };

  const handleDelete = () => {
    deleteProject(project.id, {
      onSuccess: () => {
        toastRef.current.show({
          severity: 'success',
          summary: 'Deleted',
          detail: 'Project deleted successfully',
          life: 3000,
        });
        navigate('/default/projects');
      },
      onError: (error) => {
        toastRef.current.show({
          severity: 'error',
          summary: 'Error',
          detail: error.message || 'Failed to delete project',
          life: 3000,
        });
      },
    });
  };

  const handleDeleteScreenshot = async (url) => {
    try {
      await deleteScreenshot({ projectId: project.id, screenshotUrl: url });
      toastRef.current.show({
        severity: 'success',
        summary: 'Removed',
        detail: 'Screenshot removed',
        life: 3000,
      });
    } catch (error) {
      toastRef.current.show({
        severity: 'error',
        summary: 'Error',
        detail: error.message || 'Failed to remove screenshot',
        life: 3000,
      });
    }
  };

  const handleSetCover = (screenshotUrl) => {
    setProjectCover(
      { projectId: project.id, screenshotUrl },
      {
        onSuccess: () => {
          toastRef.current.show({
            severity: 'success',
            summary: 'Cover updated',
            detail: 'This screenshot is now the project cover',
            life: 3000,
          });
        },
        onError: (error) => {
          toastRef.current.show({
            severity: 'error',
            summary: 'Error',
            detail: error.message || 'Failed to update project cover',
            life: 3000,
          });
        },
      }
    );
  };

  return (
    <>
      {!isPublicProjectRoute && (
        <ReusableSidebar
          isSidebarOpen={isSidebarOpen}
          handleToggleSidebar={() => setIsSidebarOpen(v => !v)}
          selectedPage="projects"
        />
      )}
      <MainContent isSidebarOpen={!isPublicProjectRoute && isSidebarOpen} style={themeVars}>
        <PageWrapper>
          <BackRow>
            <Button
              label="Back to Projects"
              icon="pi pi-arrow-left"
              className="p-button-text"
              onClick={() => navigate(-1)}
            />
            {isOwner && (
              <HeroToolbar>
                <Button
                  label={project.is_published ? 'Unpublish' : 'Publish'}
                  icon={project.is_published ? 'pi pi-eye-slash' : 'pi pi-eye'}
                  className="p-button-outlined p-button-sm"
                  loading={isToggling}
                  onClick={handleTogglePublish}
                />
                <Button
                  label="Edit"
                  icon="pi pi-pencil"
                  className="p-button-outlined p-button-sm"
                  onClick={() => setShowFormDialog(true)}
                />
                <Button
                  label="Delete"
                  icon="pi pi-trash"
                  className="p-button-outlined p-button-danger p-button-sm"
                  onClick={() => setDeleteConfirmVisible(true)}
                />
              </HeroToolbar>
            )}
          </BackRow>

          <HeroCard>
            <HeroCover image={coverImage} />
            <HeroContent>
              <HeroTitleBlock>
                <h1>{project.title || 'Untitled Project'}</h1>
              </HeroTitleBlock>
              <Tag
                severity={project.is_published ? 'success' : 'secondary'}
                value={project.is_published ? 'Published' : 'Draft'}
                rounded
              />
            </HeroContent>
          </HeroCard>

          <ContentStack>
            <Panel>
              <PanelHeader>
                <PanelTitle>Project Overview</PanelTitle>
                {isOwner && (
                  <AddChipButton onClick={() => setShowOverviewDialog(true)}>
                    <i className="pi pi-pencil" /> Edit
                  </AddChipButton>
                )}
              </PanelHeader>
              {hasDescription ? (
                <DescriptionText>{project.description}</DescriptionText>
              ) : (
                <EmptyMessage>No description has been added yet.</EmptyMessage>
              )}
            </Panel>

            <Panel>
              <PanelHeader>
                <PanelTitle>Skills</PanelTitle>
                {isOwner && (
                  <AddChipButton onClick={() => setShowAddSkillDialog(true)}>
                    <i className="pi pi-plus" /> Add Skill
                  </AddChipButton>
                )}
              </PanelHeader>
              {project.skills && project.skills.length > 0 ? (
                <ChipsWrap>
                  {project.skills.map((skill) => (
                    <div
                      key={skill.id}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                        padding: '0.5rem 0.75rem',
                        background: '#e0f2fe',
                        color: '#0369a1',
                        borderRadius: '16px',
                        fontSize: '0.9rem',
                        fontWeight: 500,
                        position: 'relative',
                        group: 'skill-chip',
                      }}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                        padding: '0.5rem 0.75rem',
                        background: '#e0f2fe',
                        color: '#0369a1',
                        borderRadius: '16px',
                        fontSize: '0.9rem',
                        fontWeight: 500,
                        cursor: 'default',
                      }}
                    >
                      <span>{skill.name}</span>
                      {isOwner && (
                        <i
                          className="pi pi-times"
                          onClick={(e) => {
                            e.stopPropagation();
                            setSkillToRemove(skill);
                          }}
                          style={{
                            cursor: 'pointer',
                            fontSize: '0.75rem',
                            marginLeft: '0.25rem',
                            opacity: 0.6,
                            transition: 'opacity 0.2s',
                          }}
                          onMouseEnter={(e) => (e.currentTarget.style.opacity = '1')}
                          onMouseLeave={(e) => (e.currentTarget.style.opacity = '0.6')}
                        />
                      )}
                    </div>
                  ))}
                </ChipsWrap>
              ) : (
                <EmptyMessage>No skills added for this project.</EmptyMessage>
              )}
            </Panel>

            <Panel>
              <PanelHeader>
                <PanelTitle>Project Links</PanelTitle>
                {isOwner && (
                  <AddChipButton onClick={() => setShowAddLinkDialog(true)}>
                    <i className="pi pi-plus" /> Add Link
                  </AddChipButton>
                )}
              </PanelHeader>
              {links.length > 0 ? (
                <LinksList>
                  {links.map((link, index) => (
                    <LinkRow
                      key={`${link.url}-${index}`}
                      href={link.url}
                      target="_blank"
                      rel="noreferrer"
                      style={{ position: 'relative', paddingRight: isOwner ? '2.5rem' : '0' }}
                    >
                      <span className="left">
                        <i className="pi pi-link" />
                        <span className="title">{link.title || 'Project Link'}</span>
                      </span>
                      <i className="pi pi-external-link" />
                      {isOwner && (
                        <i
                          className="pi pi-trash"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setLinkToRemove(link);
                          }}
                          style={{
                            position: 'absolute',
                            right: '0.75rem',
                            cursor: 'pointer',
                            color: '#ef4444',
                            fontSize: '0.9rem',
                            opacity: 0.6,
                            transition: 'opacity 0.2s',
                          }}
                          onMouseEnter={(e) => (e.currentTarget.style.opacity = '1')}
                          onMouseLeave={(e) => (e.currentTarget.style.opacity = '0.6')}
                        />
                      )}
                    </LinkRow>
                  ))}
                </LinksList>
              ) : (
                <EmptyMessage>No external links added yet.</EmptyMessage>
              )}
            </Panel>

            <Panel>
              <PanelHeader>
                <PanelTitle>Screenshots</PanelTitle>
                {isOwner && (
                  <AddChipButton onClick={() => setScreenshotModalVisible(true)}>
                    <i className="pi pi-plus" /> Add Screenshot
                  </AddChipButton>
                )}
              </PanelHeader>
              {screenshots.length > 0 ? (
                <ScreenshotsGrid>
                  {screenshots.map((url, index) => (
                    <ScreenshotCard key={`${url}-${index}`}>
                      <ScreenshotLink href={url} target="_blank" rel="noreferrer">
                        <ScreenshotImage src={url} alt={`Project screenshot ${index + 1}`} loading="lazy" />
                      </ScreenshotLink>
                      {isOwner && (
                        <div style={{ display: 'flex', gap: '0.4rem', alignItems: 'center', justifyContent: 'center', padding: '0.5rem' }}>
                          <Button
                            type="button"
                            label={project.cover_image_url === url ? 'Cover' : 'Set as cover'}
                            icon={project.cover_image_url === url ? 'pi pi-check' : 'pi pi-image'}
                            className="p-button-sm p-button-text"
                            disabled={project.cover_image_url === url || isSettingCover}
                            onClick={() => handleSetCover(url)}
                          />
                          <ScreenshotDeleteButton
                            type="button"
                            disabled={isDeletingScreenshot}
                            aria-label="Remove screenshot"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              handleDeleteScreenshot(url);
                            }}
                          >
                            <i className="pi pi-times" />
                          </ScreenshotDeleteButton>
                        </div>
                      )}
                    </ScreenshotCard>
                  ))}
                </ScreenshotsGrid>
              ) : (
                <EmptyMessage>No screenshots available yet.</EmptyMessage>
              )}
            </Panel>
          </ContentStack>

          {isOwner && (
            <>
              <ProjectFormDialog
                visible={showFormDialog}
                onHide={() => setShowFormDialog(false)}
                project={project}
              />

              <EditOverviewDialog
                visible={showOverviewDialog}
                onHide={() => setShowOverviewDialog(false)}
                project={project}
              />

              <AddSkillDialog
                visible={showAddSkillDialog}
                onHide={() => setShowAddSkillDialog(false)}
                project={project}
              />

              <AddLinkDialog
                visible={showAddLinkDialog}
                onHide={() => setShowAddLinkDialog(false)}
                project={project}
              />

              <RemoveSkillDialog
                visible={!!skillToRemove}
                onHide={() => setSkillToRemove(null)}
                project={project}
                skillToRemove={skillToRemove}
              />

              <RemoveLinkDialog
                visible={!!linkToRemove}
                onHide={() => setLinkToRemove(null)}
                project={project}
                linkToRemove={linkToRemove}
              />

              <ScreenshotUploader
                visible={screenshotModalVisible}
                onHide={() => setScreenshotModalVisible(false)}
                project={project}
              />

              <Dialog
                visible={deleteConfirmVisible}
                onHide={() => setDeleteConfirmVisible(false)}
                header="Confirm Delete"
                modal
                style={{ width: '400px' }}
              >
                <p>
                  Are you sure you want to delete "{project.title}"? This action cannot be undone.
                </p>
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', marginTop: '1.5rem' }}>
                  <Button
                    label="Cancel"
                    className="p-button-text p-button-secondary"
                    onClick={() => setDeleteConfirmVisible(false)}
                  />
                  <Button
                    label="Delete"
                    className="p-button-danger"
                    loading={isDeleting}
                    onClick={handleDelete}
                  />
                </div>
              </Dialog>
            </>
          )}
        </PageWrapper>
      </MainContent>
    </>
  );
}
